// v50.180 — useDocumentTitle.
//
// Sets document.title to "<title> · LabourCore" while the calling
// component is mounted; restores the previous value on unmount. Two
// usage patterns:
//
//   1. Centralised — ProtectedShell calls this with the title derived
//      from location.pathname (see titleForPath below), so every
//      protected page gets a sensible default browser-tab label
//      without per-page edits.
//
//   2. Per-page override — a page can call useDocumentTitle("Deal —
//      Slough DC2") with a row-specific name. The shell's call sets
//      the base; the page's call replaces it while mounted; unmount
//      restores the base.
//
// Why useEffect-based: <title> can't be a React Component in this app
// (no react-helmet, no Next.js Head). The pattern stays imperative.

import { useEffect } from "react";

const SUFFIX = "LabourCore";

export function useDocumentTitle(title: string | null): void {
  useEffect(() => {
    if (!title) return;
    const previous = document.title;
    document.title = `${title} · ${SUFFIX}`;
    return () => { document.title = previous; };
  }, [title]);
}

/* Route → human title map. Used by ProtectedShell to set the browser-tab
   label on every navigation. Dynamic routes (e.g. /deals/:id) get a
   static category label; individual pages can call useDocumentTitle to
   override with a row-specific name. */
const STATIC_MAP: Record<string, string> = {
  "/dashboard":     "Home",
  "/requirements":  "Requirements",
  "/deals":         "Deals",
  "/inbox":         "Inbox",
  "/notifications": "Notifications",
  "/projects":      "Projects",
  "/futures":       "Futures",
  "/workers":       "My Crew",
  "/partners":      "Supplier Pool",
  "/compliance":    "Compliance",
  "/insights":      "Insights",
  "/payments":      "Payments",
  "/earnings":      "Earnings",
  "/disputes":      "Disputes",
  "/audit":         "Audit",
  "/settings":      "Settings",
  "/onboarding":    "Onboarding",
  "/tenders":       "Open Tenders",
  "/tenders/new":   "Post Tender",
  "/tenders/me":    "My Bids",
  "/tenders/posted":"My Posted Tenders",
  "/me":            "Profile",
};

/* Patterns for dynamic routes — first match wins. */
const DYNAMIC_PATTERNS: { test: RegExp; title: string }[] = [
  { test: /^\/deals\/[^/]+/,    title: "Deal" },
  { test: /^\/projects\/[^/]+/, title: "Project" },
  { test: /^\/workers\/[^/]+/,  title: "Operative" },
  { test: /^\/partners\/[^/]+/, title: "Partner" },
  { test: /^\/coords\/[^/]+/,   title: "Coordinator" },
  { test: /^\/tenders\/[^/]+/,  title: "Tender" },
  { test: /^\/admin/,           title: "Admin" },
  { test: /^\/invitations/,     title: "Invitation" },
];

export function titleForPath(pathname: string): string {
  if (STATIC_MAP[pathname]) return STATIC_MAP[pathname];
  for (const p of DYNAMIC_PATTERNS) {
    if (p.test.test(pathname)) return p.title;
  }
  return "";
}
